'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { IconFolder, IconDatabase } from '@tabler/icons-react';
import { useProjectsData } from '@/hooks/useProjectsData';
import { DC_REFRESH_EVENT } from '@/lib/dashboard/header-config';
import { ROUTES } from '@/lib/routes';
import { DashboardKpiStrip } from '@/components/dashboard/DashboardKpiStrip';
import { DashboardCharts } from '@/components/dashboard/DashboardCharts';
import { SourceHealthCard } from '@/components/dashboard/SourceHealthCard';
import { InsightCards } from '@/components/dashboard/InsightCards';
import { RecentProjectsCard } from '@/components/dashboard/RecentProjectsCard';
import { DashboardPageSkeleton } from '@/components/dashboard/states/DashboardPageSkeleton';
import { DashboardEmptyState } from '@/components/dashboard/states/DashboardEmptyState';
import { DashboardErrorState } from '@/components/dashboard/states/DashboardErrorState';

export default function DashboardPage() {
  const router = useRouter();
  const { projects, sourceStatuses, isLoading, error, refetch } =
    useProjectsData();

  // Listen for refresh event from layout header
  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DC_REFRESH_EVENT, handler);
    return () => window.removeEventListener(DC_REFRESH_EVENT, handler);
  }, [refetch]);

  // Loading
  if (isLoading) {
    return <DashboardPageSkeleton />;
  }

  // Hard error
  if (error) {
    return (
      <DashboardErrorState
        error={error}
        onRetry={refetch}
        sourceStatuses={sourceStatuses}
      />
    );
  }

  // Empty
  if (projects.length === 0) {
    return (
      <DashboardEmptyState
        onGoProjects={() => router.push(ROUTES.projects)}
      />
    );
  }

  return (
    <div className="p-4 lg:p-6">
      {/* KPI strip */}
      <DashboardKpiStrip
        projects={projects}
        sourceStatuses={sourceStatuses}
      />

      {/* Main grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
        {/* Left column — charts */}
        <DashboardCharts projects={projects} />

        {/* Right column — health + insights */}
        <div className="space-y-6">
          <SourceHealthCard sourceStatuses={sourceStatuses} />
          <InsightCards projects={projects} />
          <RecentProjectsCard projects={projects} />
        </div>
      </div>

      {/* Quick links */}
      <Card className="mt-6">
        <CardContent className="pt-0 flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground mr-2">Quick links</span>
          <Button variant="outline" size="sm" asChild>
            <Link href={ROUTES.projects}>
              <IconFolder className="size-4 mr-1.5" />
              Explore Projects
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link href={ROUTES.sources}>
              <IconDatabase className="size-4 mr-1.5" />
              View Sources
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
